import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const lineWrap = {
  hidden: {},
  show: (i) => ({ transition: { staggerChildren: 0.14, delayChildren: 0.35 + i * 0.14 } }),
};
const lineInner = {
  hidden: { y: "115%", rotate: 2.5 },
  show: { y: "0%", rotate: 0, transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } },
};

function MaskedLine({ index, className, children }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className={`block origin-left ${className}`}
        variants={lineInner}
        custom={index}
        initial="hidden"
        animate="show"
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + index * 0.14 }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function HeroSection({ onRequestInvite }) {
  const [email, setEmail] = useState("");
  const cardRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 16 });

  const { scrollYProgress } = useScroll({ offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const stampY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const onTilt = (e) => {
    const r = cardRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const [submitting, setSubmitting] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.includes("@") || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "hero" }),
      });
      const data = await res.json();
      if (data.status === "existing") {
        toast.info(`Welcome back — credential ${data.credential} is already held in your name.`);
      } else {
        toast.success(`Stamped into the collector ledger as ${data.credential}. We'll write soon.`);
      }
      setEmail("");
    } catch {
      toast.error("The ledger is unreachable — please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
    >
      <div className="pointer-events-none absolute -right-24 top-24 hidden select-none font-serif text-[22rem] leading-none text-[#171412]/[0.04] lg:block">
        OG
      </div>

      <div className="mx-auto grid w-full max-w-[1600px] grid-cols-12 items-center gap-10 px-6 sm:px-12 lg:px-20">
        <div className="col-span-12 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8 flex items-center gap-3 font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-[#6B1D2F]"
          >
            <span className="inline-block h-px w-10 bg-[#6B1D2F]" />
            Authenticated Vintage Marketplace — Opening Soon
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif text-[13vw] leading-[0.98] tracking-tight text-[#1C1815] sm:text-6xl lg:text-7xl xl:text-[5.4rem]"
          >
            <MaskedLine index={0}>Every piece</MaskedLine>
            <MaskedLine index={1}>has a past.</MaskedLine>
            <MaskedLine index={2} className="italic text-[#6B1D2F]">
              Find its future.
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-md text-base leading-relaxed text-[#5C544D]"
          >
            Yesterday's treasures, today's finds. OldenGoods is curating vetted sellers and
            authenticated clothing, decor, and collectibles into one calm place to browse —
            while the vault doors are still being fitted.
          </motion.p>

          <motion.form
            onSubmit={submitEmail}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex max-w-md items-stretch border border-[#1C1815] bg-[#FCF9F5]"
          >
            <input
              data-testid="hero-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@correspondence.com"
              className="w-full bg-transparent px-4 py-3.5 font-mono text-xs tracking-wider text-[#1C1815] outline-none placeholder:text-[#8A8075]"
            />
            <button
              data-testid="hero-submit-btn"
              type="submit"
              disabled={submitting}
              className="group flex shrink-0 items-center gap-2 bg-[#6B1D2F] px-5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#FAF6F0] transition-colors duration-300 hover:bg-[#521422] disabled:opacity-60"
            >
              {submitting ? "Stamping…" : "Notify Me"}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A8075]"
          >
            <span>Vetted Sellers</span>
            <span className="text-[#C59B27]">✦</span>
            <span>Authenticated Items</span>
            <span className="text-[#C59B27]">✦</span>
            <span>Buyer Protection</span>
          </motion.div>
        </div>

        <div className="relative col-span-12 mt-6 lg:col-span-5 lg:mt-0" style={{ perspective: 1200 }}>
          <motion.div
            style={{ y: parallaxY }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              ref={cardRef}
              data-testid="hero-3d-card"
              onMouseMove={onTilt}
              onMouseLeave={() => { mx.set(0); my.set(0); }}
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative mx-auto max-w-md border border-[#E2D9CD] bg-[#FCF9F5] p-4 shadow-[0_40px_80px_-30px_rgba(23,20,18,0.35)]"
            >
              <div className="overflow-hidden" style={{ transform: "translateZ(30px)" }}>
                <img
                  src="/images/product-01.jpg"
                  alt="1960s starburst wall clock in teak and brass"
                  className="aspect-square w-full object-cover"
                />
              </div>
              <div className="mt-4 flex items-end justify-between" style={{ transform: "translateZ(20px)" }}>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A8075]">Ref. OG-1962-STB</p>
                  <p className="mt-1 font-serif text-lg text-[#1C1815]">Starburst Wall Clock</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B1D2F]">1960s · Verified</p>
                </div>
                <p className="font-serif text-2xl text-[#1C1815]">$275</p>
              </div>
              <div className="absolute -left-3 top-8 flex items-center gap-1.5 border border-[#C59B27]/60 bg-[#FAF6F0] px-2.5 py-1.5" style={{ transform: "translateZ(45px)" }}>
                <BadgeCheck className="h-3.5 w-3.5 text-[#C59B27]" />
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#1C1815]">Authenticated</span>
              </div>
            </motion.div>
          </motion.div>

          {/* rotating under-construction stamp */}
          <motion.button
            data-testid="hero-stamp-seal"
            onClick={onRequestInvite}
            style={{ y: stampY }}
            initial={{ opacity: 0, scale: 0.6, rotate: -18 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 1.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-8 -left-4 z-10 h-32 w-32 cursor-pointer sm:-left-10 lg:-bottom-10"
            aria-label="Under construction — request an invite"
          >
            <svg viewBox="0 0 120 120" className="animate-spin-slow h-full w-full">
              <defs>
                <path id="stamp-circle" d="M 60,60 m -44,0 a 44,44 0 1,1 88,0 a 44,44 0 1,1 -88,0" />
              </defs>
              <circle cx="60" cy="60" r="58" fill="#6B1D2F" />
              <circle cx="60" cy="60" r="52" fill="none" stroke="#FAF6F0" strokeWidth="1" strokeDasharray="2 3" />
              <text fill="#FAF6F0" fontSize="10.5" fontFamily="'IBM Plex Mono', monospace" letterSpacing="2.5">
                <textPath href="#stamp-circle">UNDER CONSTRUCTION • OPENING SOON •</textPath>
              </text>
              <text x="60" y="66" textAnchor="middle" fill="#C59B27" fontSize="15" fontFamily="'Playfair Display', serif" fontStyle="italic">
                OG
              </text>
            </svg>
          </motion.button>
        </div>
      </div>
    </section>
  );
}
